import React, { useEffect, useMemo, useState } from "react"
import Client from "../../models/client"
import { buildPageGroupPayload, TemplateItem } from "../utils/pageGroupPayload"
import useSWR, { useSWRConfig } from "swr"
import { FETCH_KEYS, fetchPageGroup, fetchPagePreview, fetchTemplates, updateClient, updateLINEMessage, updatePageGroup } from "../api"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, Loader, NumberInput, Radio, ScrollArea, Stack, Text } from "@mantine/core"
import { DateTimePicker } from "@mantine/dates"
import { ImageKind, TemplatePageFormValues, TemplatePageType, UploadedImages } from "../utils/templatePageForm"
import { buildValidate } from "../utils/templatePageValidation"
import CommonTemplateEditor from "./CommonTemplateEditor"
import InviterTemplateEditor from "./InviterTemplateEditor"
import GuestTemplateEditor from "./GuestTemplateEditor"
import { confirmDiscardChanges, confirmUnpublishPage } from "../utils/confirm"

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
  pageGroupId: number | null
  templatePageType: TemplatePageType
  onSaved?: () => void
}

/** モーダルのタイトル */
const MODAL_TITLES: Record<TemplatePageType, string> = {
  common: '紹介キャンペーンページ 共通設定',
  inviter: '紹介者（inviter）ページ設定',
  guest: '友人（guest）ページ設定',
}

/** 画像の種類とフォームのフィールド名の対応 */
const IMAGE_FIELD_NAMES: Record<ImageKind, string> = {
  KEY_VISUAL: 'key_visual',
  LOGO: 'logo',
  INCENTIVE_IMAGE: 'incentive_image',
  INCENTIVE_IMAGE_GUEST: 'incentive_image_guest',
}

const EMPTY_IMAGES: UploadedImages = {
  KEY_VISUAL: null,
  LOGO: null,
  INCENTIVE_IMAGE: null,
  INCENTIVE_IMAGE_GUEST: null,
}

const defaultValues: TemplatePageFormValues = {
  pageDescription: '',
  enabled: '0',
  bodyHtml: '',
  startDate: '',
  endDate: '',
  enable_web_cv: false,
  enable_coming_cv: false,
  key_visual: '',
  key_visual_text: '',
  link_text: '',
  link_url: '',
  flow_text: '',
  notice: '',
  terms: '',
  privacy_policy: '',
  tag: '',
  template_type: 0,
  logo: '',
  incentive_action: '',
  incentive_content: '',
  incentive_amount: '',
  incentive_image: '',
  incentive_content_guest: '',
  incentive_amount_guest: '',
  incentive_image_guest: '',
  theme_color: '',
  button_color: '',
  guest_limit: 0,
  line_message: '',
  target_cv_num: 0,
  coming_name: false,
  coming_mail: false,
  coming_tel: false,
  coming_other: false,
  coming_address: false,
}

/** サーバが返す日時からタイムゾーンオフセットを切り捨てる。解釈させるとブラウザのTZに変換され、JST以外の端末で日時がずれるため */
const toWallClock = (value: string | null | undefined): string =>
  value ? value.slice(0, 19).replace('T', ' ') : ''

/** page_group と client からフォームの初期値を組み立てる */
const buildInitialValues = (pageGroup: any, client: Client): TemplatePageFormValues => ({
  pageDescription: pageGroup.page_description ?? '',
  enabled: String(Number(pageGroup.enabled)),
  bodyHtml: pageGroup.body_html ?? '',
  startDate: toWallClock(pageGroup.start_date),
  endDate: toWallClock(pageGroup.end_date),
  enable_web_cv: pageGroup.enable_web_cv,
  enable_coming_cv: pageGroup.enable_coming_cv,
  key_visual: pageGroup.key_visual ?? '',
  key_visual_text: pageGroup.key_visual_text ?? '',
  link_text: pageGroup.link_text || pageGroup.incentive_action || '',
  link_url: pageGroup.link_url ?? '',
  flow_text: pageGroup.flow_text ?? '',
  notice: pageGroup.notice ?? '',
  terms: pageGroup.terms ?? '',
  privacy_policy: pageGroup.privacy_policy ?? '',
  tag: pageGroup.tag ?? '',
  template_type: parseInt(pageGroup.template_type, 10),
  logo: pageGroup.logo ?? '',
  incentive_action: pageGroup.incentive_action || '無料体験を予約',
  incentive_content: pageGroup.incentive_content || 'Amazonギフト',
  incentive_amount: pageGroup.incentive_amount ?? '',
  incentive_image: pageGroup.incentive_image ?? '',
  incentive_content_guest: pageGroup.incentive_content_guest || 'Amazonギフト',
  incentive_amount_guest: pageGroup.incentive_amount_guest ?? '',
  incentive_image_guest: pageGroup.incentive_image_guest ?? '',
  theme_color: pageGroup.theme_color ?? '',
  button_color: pageGroup.button_color ?? '',
  guest_limit: pageGroup.guest_limit ?? 0,
  line_message: client.line_message,
  target_cv_num: client.target_cv_num,
  coming_name: client.coming_name,
  coming_mail: client.coming_mail,
  coming_tel: client.coming_tel,
  coming_other: client.coming_other,
  coming_address: client.coming_address,
})

/** client 更新用のペイロード。target_cv_num を truthy 判定にしているのは旧 updateClient thunk の踏襲（0 は送らない） */
const buildClientPayload = (values: TemplatePageFormValues): Record<string, any> => {
  let payload: Record<string, any> = {}
  if (values.target_cv_num) payload = { target_cv_num: values.target_cv_num }
  if (values.coming_name !== null) payload = { ...payload, coming_name: values.coming_name }
  if (values.coming_mail !== null) payload = { ...payload, coming_mail: values.coming_mail }
  if (values.coming_tel !== null) payload = { ...payload, coming_tel: values.coming_tel }
  if (values.coming_other !== null) payload = { ...payload, coming_other: values.coming_other }
  if (values.coming_address !== null) payload = { ...payload, coming_address: values.coming_address }
  return payload
}

const TemplatePageFormModal: React.FC<Props> = ({
  opened,
  onClose,
  client,
  pageGroupId,
  templatePageType,
  onSaved,
}) => {
  const { mutate } = useSWRConfig()
  const [submitting, setSubmitting] = useState(false)
  const [initializedFor, setInitializedFor] = useState<number | null>(null)
  const [images, setImages] = useState<UploadedImages>(EMPTY_IMAGES)
  const [preview, setPreview] = useState('')

  const hasPublishSetting = templatePageType === 'inviter' || templatePageType === 'guest'
  const isGuest = templatePageType === 'guest'

  const { data: pageGroup } = useSWR(
    opened && pageGroupId !== null ? FETCH_KEYS.pageGroup(client.id, pageGroupId) : null,
    () => fetchPageGroup(client.id, pageGroupId as number),
  )

  const { data: templates } = useSWR<TemplateItem[]>(
    opened ? '/admin/api/templates' : null,
    fetchTemplates,
  )

  // モーダルは開きなおしでもアンマウントされないため、templatePageType の変化にルールを追従させる
  const validate = useMemo(() => buildValidate(templatePageType), [templatePageType])

  const form = useForm<TemplatePageFormValues>({
    initialValues: defaultValues,
    validate,
    validateInputOnBlur: true,
    validateInputOnChange: ['logo', 'key_visual', 'incentive_image', 'incentive_image_guest'],
  })

  // page_group と client が揃ったら初期値を流し込む。
  // 再検証で新しいデータが届いたときも同期しなおすが、編集中の値は上書きしない
  useEffect(() => {
    if (!opened || !pageGroup) return
    if (initializedFor === pageGroupId && form.isDirty()) return
    if (initializedFor !== pageGroupId) {
      // 前のページのアップロード画像・プレビューが残っていれば破棄する
      Object.values(images).forEach((image) => image && URL.revokeObjectURL(image.preview))
      setImages(EMPTY_IMAGES)
      setPreview('')
    }
    form.setValues(buildInitialValues(pageGroup, client))
    form.resetDirty()
    setInitializedFor(pageGroupId)
  }, [opened, pageGroup, client])

  // フォームの値が変わるたびにプレビューを取り直す
  useEffect(() => {
    if (pageGroupId === null || initializedFor !== pageGroupId) return
    let stale = false
    fetchPagePreview(pageGroupId, form.values)
      .then((html) => {
        // 遅れて返ってきたリクエストで新しいプレビューを上書きしない
        if (!stale) setPreview(html)
      })
      .catch((e) => {
        // プレビューの失敗は保存を妨げないため通知しない
        console.error('プレビューの取得に失敗しました', e)
      })
    return () => {
      stale = true
    }
  }, [initializedFor, pageGroupId, form.values])

  const handleUploadImage = (kind: ImageKind, files: File[]) => {
    const file = files[0]
    if (!file) return

    // プレビューAPI（page_groups#preview）はフォーム値の [0].preview から blob URL を読むため、
    // File 自体にも preview を持たせて JSON.stringify で届くようにする
    const preview = URL.createObjectURL(file)
    setImages((prev) => {
      if (prev[kind]) URL.revokeObjectURL(prev[kind]!.preview)
      return { ...prev, [kind]: { file, preview } }
    })
    form.setFieldValue(IMAGE_FIELD_NAMES[kind], [Object.assign(file, { preview })])
  }

  const handleClearImage = (kind: ImageKind) => {
    setImages((prev) => {
      if (prev[kind]) URL.revokeObjectURL(prev[kind]!.preview)
      return { ...prev, [kind]: null }
    })
    form.setFieldValue(IMAGE_FIELD_NAMES[kind], '')
  }

  const handleClose = () => {
    if (form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const handleExited = () => {
    Object.values(images).forEach((image) => image && URL.revokeObjectURL(image.preview))
    setImages(EMPTY_IMAGES)
    setPreview('')
    setInitializedFor(null)
    form.setValues(defaultValues)
    form.resetDirty()
  }

  /** バリデーションで送信が止まったとき、理由がわかるように通知して最初のエラー欄へ飛ばす */
  const handleValidationFailure = (errors: typeof form.errors) => {
    const nodes = Object.keys(errors)
      .map((path) => form.getInputNode(path))
      .filter((node): node is HTMLElement => Boolean(node))

    const firstNode = nodes.reduce<HTMLElement | null>((found, node) => {
      if (!found) return node
      const nodeComesFirst = Boolean(
        found.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING,
      )
      return nodeComesFirst ? node : found
    }, null)

    firstNode?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    notifications.show({
      message: '入力内容にエラーがあります。赤字の項目を確認してください',
      color: 'red',
    })
  }

  const handleSubmit = async (values: TemplatePageFormValues) => {
    if (pageGroupId === null) return

    // 公開設定UIを持たない common では wasEnabled と willBeEnabled が一致するため発火しない
    const wasEnabled = Number(pageGroup?.enabled) === 1
    const willBeEnabled = values.enabled === '1'
    if (
      wasEnabled &&
      !willBeEnabled &&
      !confirmUnpublishPage()
    ) {
      return
    }

    setSubmitting(true)
    try {
      const payload = buildPageGroupPayload(values, templates)
      await updatePageGroup(
        client.id,
        pageGroupId,
        payload.pageGroup,
        payload.keyVisual,
        payload.logo,
        payload.incentiveImage,
        payload.incentiveImageGuest,
      )

      // LINEメッセージと client はどちらも同じ /clients/:id を PATCH するため直列で呼ぶ
      if (values.line_message != null) {
        await updateLINEMessage(client.id, values.line_message)
      }
      const shouldUpdateClient =
        values.target_cv_num != null ||
        values.coming_name != null ||
        values.coming_mail != null ||
        values.coming_tel != null ||
        values.coming_other != null ||
        values.coming_address != null
      if (shouldUpdateClient) {
        await updateClient(client.id, buildClientPayload(values))
      }

      // 開きなおしたときに古い値が出ないよう、この page_group のキャッシュも更新する
      await Promise.all([
        mutate(FETCH_KEYS.fetchClient(client.id)),
        mutate(FETCH_KEYS.pageGroup(client.id, pageGroupId)),
      ])

      // 保存済みの内容が「未保存の変更」扱いで残らないようにする
      form.resetDirty()

      notifications.show({ message: 'ページ設定を保存しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (e: any) {
      console.error('ページ設定の保存に失敗しました', e.response?.status, e.response?.data)
      const errData = e.response?.data
      const msg = errData?.errors?.join('\n') ?? errData?.error ?? '保存に失敗しました'
      notifications.show({ message: msg, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  const showLoader = pageGroupId === null || initializedFor !== pageGroupId || !templates

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title={MODAL_TITLES[templatePageType]}
      width={1200}
      size="85vh"
      onExited={handleExited}
    >
      {showLoader ? (
        <Group justify="center" py="xl" style={{ width: '100%' }}>
          <Loader />
        </Group>
      ) : (
        <form
          onSubmit={form.onSubmit(handleSubmit, handleValidationFailure)}
          onKeyDown={(e) => {
            // 入力欄でのEnterによる暗黙送信を止める
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault()
            }
          }}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <Group align="stretch" gap={0} wrap="nowrap" style={{ flex: 1, minHeight: 0 }}>
            <ScrollArea style={{ flex: '1 1 60%', minHeight: 0 }} bg="#00000008" px={24}>
              <Stack gap="md" p="md">
                {templatePageType === 'common' ? (
                  <CommonTemplateEditor
                    form={form}
                    images={images}
                    templates={templates}
                    onUploadImage={handleUploadImage}
                    onClearImage={handleClearImage}
                  />
                ) : templatePageType === 'inviter' ? (
                  <InviterTemplateEditor
                    form={form}
                    images={images}
                    onUploadImage={handleUploadImage}
                    onClearImage={handleClearImage}
                  />
                ) : (
                  <GuestTemplateEditor
                    form={form}
                    images={images}
                    invyServiceUse={client.invy_service_use}
                    onUploadImage={handleUploadImage}
                    onClearImage={handleClearImage}
                  />
                )}

                {isGuest && (
                  <NumberInput
                    label={
                      <>
                        <span>1回のシェアで紹介できる人数　※「0」の場合は無制限</span>
                        {requiredMark}
                      </>
                    }
                    styles={requireLabelStyles}
                    min={0}
                    allowNegative={false}
                    {...form.getInputProps('guest_limit')}
                  />
                )}

                {hasPublishSetting && (
                  <>
                    <DateTimePicker
                      label={<><span>開始日</span>{requiredMark}</>}
                      styles={requireLabelStyles}
                      valueFormat="YYYY-MM-DD HH:mm"
                      {...form.getInputProps('startDate')}
                      value={form.values.startDate || null}
                      onChange={(v) => form.setFieldValue('startDate', v ?? '')}
                      clearable
                      locale="ja"
                    />
                    <DateTimePicker
                      label={<><span>終了日</span>{requiredMark}</>}
                      styles={requireLabelStyles}
                      valueFormat="YYYY-MM-DD HH:mm"
                      {...form.getInputProps('endDate')}
                      value={form.values.endDate || null}
                      onChange={(v) => form.setFieldValue('endDate', v ?? '')}
                      minDate={form.values.startDate || undefined}
                      clearable
                      locale="ja"
                    />

                    <Radio.Group label="公開設定" {...form.getInputProps('enabled')}>
                      <Group mt="xs">
                        <Radio value="0" label="非公開" />
                        <Radio value="1" label="公開" />
                      </Group>
                    </Radio.Group>
                  </>
                )}
              </Stack>
            </ScrollArea>

            <Box p="md" style={{ flex: '1 1 40%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Text size="sm" fw={500} mb={8}>
                プレビュー
              </Text>
              <ScrollArea
                style={{ flex: 1, minHeight: 0, borderRadius: 8, backgroundColor: '#A7A3AA' }}
                p={10}
              >
                <div
                  style={{ backgroundColor: '#fff', zoom: '75%', pointerEvents: 'none' }}
                  dangerouslySetInnerHTML={{ __html: preview ? preview : ' ' }}
                />
              </ScrollArea>
            </Box>
          </Group>

          <Box
            p="md"
            style={{ borderTop: '1px solid #0000001A', backgroundColor: '#fff', flexShrink: 0 }}
          >
            <Group justify="flex-end" gap={8}>
              <Button
                variant="filled"
                radius="xl"
                type="submit"
                loading={submitting}
              >
                保存
              </Button>
            </Group>
          </Box>
        </form>
      )}
    </BaseModal>
  )
}

export default TemplatePageFormModal
